# UI Affordances

Extend Taias beyond LLM hints to control which UI components appear in your widgets.

---

## Why UI Affordances Exist

Taias already centralizes your flow logic - defining what tool should come next. But there's a gap: **the UI expression of "what's next" is still scattered across your widgets.**

### The Problems

**Scattered next-step UI logic**

Your flow says "after `get_recent_tracks`, suggest `get_recommendations`." But each widget independently decides how to render that. One widget might show a "Get Recommendations" button, another might not show anything, and a third might show something completely different.

**Low-leverage changes**

Want to change what happens after a user views their tracks? You update your flow in one place, but then you have to hunt through every widget that might render a CTA related to that step.

**Drift over time**

As your app grows, widgets start interpreting "next" differently. Without a central source of truth, inconsistencies creep in and nobody notices until the UX feels disjointed.

**Hard to debug**

When the UI is wrong, it's unclear whether:

- The flow decision was wrong
- The UI mapping was missing
- The widget logic overrode it

### The Goal

**Make a small, centralized change -> reliably change behavior across many widgets.**

UI Affordances let you:

1. Register what UI options exist for each widget slot
2. Let Taias select the right option based on your flow
3. Have widgets render by selection, not by scattered conditional logic

---

## Core Concepts

### Slots

A **slot** is a UI position in a widget that can present an affordance. Taias provides three default slots:

| Slot            | Purpose                            |
| --------------- | ---------------------------------- |
| `primaryCta`    | The main call-to-action button     |
| `secondaryCta`  | A secondary/alternative action     |
| `widgetVariant` | The overall widget layout or style |

You can also define **custom slots** for more complex widgets. See [Advanced Patterns](/docs#advanced-patterns) for details.

Slots answer: _"Where in the UI are we influencing behavior?"_

### Handles

A **handle** is a concrete, selectable affordance option. Each handle has:

- `handleId` - A unique identifier (e.g., `"playlist.cta.recommend"`)
- `slot` - Which slot this handle can fill
- `bindsTo` - The condition for when this handle should be selected

Handles answer: _"Which specific UI affordance are we referring to?"_

### Bindings

A **binding** is the selection condition for a handle. Bindings can use the shorthand `toolName` format or the generalized `{ key, value }` format:

```ts
// Shorthand - matches decision.nextTool
bindsTo: { toolName: "get_recommendations" }

// Generalized - matches any decision field
bindsTo: { key: "secondaryAction", value: "manual_setup" }
```

This means: _"Select this handle when the specified decision field equals this value."_

### Registry

The **registry** is the central inventory of all your registered handles. You assemble it once and pass it to `createTaias`.

### Selections

**Selections** are the output - the handles Taias chose for each slot based on your flow decision. These get passed to widgets via `structuredContent.taias.selections`.

---

## Critical Mental Model

### What `bindsTo.toolName` Really Means

This is important to understand correctly.

`bindsTo.toolName` does **NOT** mean:

> "This affordance will call this tool."

It means:

> "If this tool is the next step in the flow, this is the affordance to show in this slot."

It's a **selection key**, not an execution contract.

### Why This Matters

An affordance might:

- Call the bound tool (common for primary CTAs)
- Call a different tool entirely
- Call multiple tools
- Open a modal or external link
- Just change layout/styling with no action at all

So `bindsTo.toolName` is about **when the affordance appears**, not **what it does**.

### Slot-Specific Intuition

| Slot            | Typical Behavior                                                                           |
| --------------- | ------------------------------------------------------------------------------------------ |
| `primaryCta`    | Often calls the same tool as `bindsTo.toolName` - but that's convention, not requirement   |
| `secondaryCta`  | Usually calls a _different_ tool, using `bindsTo.toolName` only as the selection condition |
| `widgetVariant` | May have no actions at all - purely visual, selected by flow state                         |

---

## How It Works

```
Flow Decision (nextTool)
        ↓
Affordance Selection (match bindsTo.toolName)
        ↓
structuredContent.taias.selections
        ↓
Widget renders by handleId
```

1. Your flow returns `{ nextTool: "get_recommendations" }`
2. Taias looks through registered handles for each slot
3. For each slot, it selects the handle where `bindsTo.toolName === "get_recommendations"`
4. Selected handles are returned in `selections`
5. Your tool injects `selections` into `structuredContent`
6. Your widget reads `selections` and renders the matching UI

---

## Integration Example

Using the music discovery flow from the quickstart:

### 1. Define Affordances

```ts
// src/taias/affordances.ts

export const trackListAffordances = defineAffordances((r) => {
  // Primary CTA options - only one will be selected based on flow
  r.primaryCta("tracks.cta.recommend", { toolName: "get_recommendations" });
  r.primaryCta("tracks.cta.playlist", { toolName: "create_playlist" });

  // Widget variant options
  r.widgetVariant("tracks.variant.discovery", {
    toolName: "get_recommendations",
  });
  r.widgetVariant("tracks.variant.playlist", { toolName: "create_playlist" });
});
```

### 2. Assemble Registry

```ts
// src/taias/registry.ts

export const registry = mergeAffordances([trackListAffordances]);
```

### 3. Create Taias Instance

```ts
// src/taias/instance.ts

export const taias = createTaias({
  flow: musicDiscoveryFlow,
  affordances: registry,
  devMode: true,
});
```

### 4. Inject in Tool Handler

```ts
const affordances = await taias.resolve({ toolName: "get_recent_tracks" });

return {
  content: [{ type: "text", text: message }],
  structuredContent: {
    _widget: "track-list",
    tracks,
    taias: { selections: affordances?.selections ?? {} },
  },
};
```

### 5. Render in Widget

```tsx
function TrackListWidget({ toolResult }) {
  const selections = toolResult?.structuredContent?.taias?.selections ?? {};

  const primaryId = selections.primaryCta?.handleId;
  const variantId = selections.widgetVariant?.handleId;

  return (
    <div
      className={
        variantId === "tracks.variant.discovery"
          ? "discovery-mode"
          : "playlist-mode"
      }
    >
      {/* Track list UI */}

      {primaryId === "tracks.cta.recommend" && (
        <Button onClick={() => callTool("get_recommendations", {})}>
          Get Recommendations
        </Button>
      )}

      {primaryId === "tracks.cta.playlist" && (
        <Button onClick={() => callTool("create_playlist", {})}>
          Create Playlist
        </Button>
      )}
    </div>
  );
}
```

---

## The Leverage

When you change your flow:

```ts
// Before: after viewing tracks, suggest recommendations
flow.step(
  { toolName: { is: "get_recent_tracks" } },
  { nextTool: "get_recommendations" },
);

// After: after viewing tracks, suggest creating a playlist
flow.step(
  { toolName: { is: "get_recent_tracks" } },
  { nextTool: "create_playlist" },
);
```

The widget automatically shows "Create Playlist" instead of "Get Recommendations" - **with no widget code changes**.

This is the leverage: centralized flow logic -> automatic UI adaptation.

---

## Boundaries

For multi-field decisions and custom slots, see [Advanced Patterns](/docs#advanced-patterns).

### Selection is deterministic

For any `{slot, key, value}` triple, only one handle can exist. This prevents ambiguity.

### Multiple decision fields

Logic statements can return however many and whichever fields (not just `nextTool`). Each binding declares which decision field it matches via `{ key, value }` format - Taias automatically infers the mapping for each slot, enabling independent UI control.

### All handles for a slot must use the same key

If you register handles for the same slot with different keys, Taias throws an error. This ensures consistent behavior.

### Custom slots are fully typed

When you define custom slots via generics, TypeScript ensures only valid slot names are used throughout your code.

### Widgets own rendering

Taias selects handles. Widgets decide how to render them. Labels, layouts, styling - all remain under widget control.
